import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

export const badgeVariants = cva(
  'inline-flex items-center whitespace-nowrap font-medium',
  {
    variants: {
      size: {
        sm: 'gap-1 px-2 py-0.5 text-[10px]',
        md: 'gap-1.5 px-2.5 py-1 text-xs',
      },
      /** pill for status badges; square sits in toolbars next to buttons */
      shape: {
        pill: 'rounded-full',
        square: 'rounded-md',
      },
      color: {
        gray: 'bg-gray-500/10 text-gray-600',
        green: 'bg-green-600/10 text-green-600',
        blue: 'bg-blue-600/10 text-blue-600',
        amber: 'bg-amber-600/10 text-amber-600',
        orange: 'bg-orange-500/10 text-orange-600',
        red: 'bg-red-600/10 text-red-600',
        purple: 'bg-purple-600/10 text-purple-600',
        slate: 'bg-gray-700/10 text-gray-700',
      },
    },
    defaultVariants: {
      color: 'gray',
      size: 'md',
      shape: 'pill',
    },
  },
);

const dotVariants = cva('h-1.5 w-1.5 shrink-0 rounded-full', {
  variants: {
    color: {
      gray: 'bg-gray-500',
      green: 'bg-green-600',
      blue: 'bg-blue-600',
      amber: 'bg-amber-600',
      orange: 'bg-orange-500',
      red: 'bg-red-600',
      purple: 'bg-purple-600',
      slate: 'bg-gray-700',
    },
  },
  defaultVariants: {
    color: 'gray',
  },
});

type KoobBadgeProps = Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  'className' | 'color'
> &
  VariantProps<typeof badgeVariants> & {
    /** Leading status dot, for state badges (confirmed, pending, …). */
    dot?: boolean;
  };

const KoobBadge = React.forwardRef<HTMLSpanElement, KoobBadgeProps>(
  ({ color, size, shape, dot = false, children, ...props }, ref) => (
    <span
      ref={ref}
      className={badgeVariants({ color, size, shape })}
      {...props}
    >
      {dot && <span className={dotVariants({ color })} />}
      {children}
    </span>
  ),
);

KoobBadge.displayName = 'KoobBadge';

export default KoobBadge;
